import Navigation from 'components/navigation';
import { INTERRUPT, PATH } from 'config';
import constate from 'constate';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { TIMEOUT, UNKNOWN } from 'service/error-handler';
import repo from 'service/repository';
import validator from 'validator';

const UseChargingRecords = () => {
  const { serialNumber, id } = useParams();
  const [loaded, setLoaded] = useState(false);
  const [chargingData, setChargingData] = useState();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [modifyEmailDialogShow, setModifyEmailDialogShow] = useState(false);
  const [geolocation, setGeolocation] = useState({});
  const [geolocationFetchFail, setGeolocationFetchFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [mailModifying, setMailModifying] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const { navigateToLocation, NavigationMapDialog } = Navigation(geolocation);

  const fetchGeoLocation = async () => {
    if (Object.keys(geolocation).length === 0) {
      const [error, data] = await repo.getEvses(serialNumber);
      if (error) {
        return setGeolocationFetchFail(true);
      }

      setGeolocationFetchFail(false);
      setGeolocation(() => ({ lat: data.store.lat, lng: data.store.lng }));
    }
    navigateToLocation();
  };

  const fetchChargingRecord = useCallback(async () => {
    if (!loaded && location.state !== undefined) {
      setChargingData(location.state.data);
      console.log('[ChargingRecords] 從 location.state 取得充電記錄:', {
        id: location.state.data.id,
        email: location.state.data.email,
        userPhone: location.state.data.userPhone || location.state.data.UserPhone,
      });
      history.replace(location.pathname); // 清除上一步驟帶過來的 location.state
    } else {
      const [error, data] = await repo.getChargingRecord(id);
      if (error) {
        // 如果是回傳 5xx 則讓使用者繼續等待
        if (error.toJSON().message === 'Network Error') {
          return;
        }

        return history.replace({
          pathname: `${PATH.EVSES}/${serialNumber}${PATH.ERROR}`,
          search: `error=${UNKNOWN}`,
          state: { error: JSON.stringify(error.toJSON()) },
        });
      }

      if (data.state === INTERRUPT) {
        return history.replace({ pathname: `${PATH.EVSES}/${serialNumber}${PATH.ERROR}`, search: `error=${TIMEOUT}` });
      }

      setChargingData(data);
    }

    setLoaded(true);
    setRefreshing(false);
  }, [loaded]); //eslint-disable-line

  useEffect(() => {
    fetchChargingRecord();
  }, []); //eslint-disable-line

  const modifyContact = async () => {
    // 清除舊的錯誤訊息
    setErrorMessage('');

    // 驗證 email 和 phone
    const hasEmail = email && email.trim() !== '';
    const hasPhone = phone && phone.trim() !== '';
    const isValidEmail = hasEmail && validator.isEmail(email);
    const isValidPhone = hasPhone; // 手機號碼格式暫時不嚴格驗證

    // 至少要有一個聯絡方式
    if (!hasEmail && !hasPhone) {
      return setErrorMessage('請至少填寫一個有效的聯絡方式');
    }

    // 只檢查有填寫的欄位
    if (hasEmail && !isValidEmail) {
      return setErrorMessage('Email 格式錯誤');
    }

    setMailModifying(true);
    const updateData = { id };
    if (isValidEmail) updateData.email = email;
    if (isValidPhone) updateData.phone = phone;

    const [error, data] = await repo.updateChargingRecord(updateData);
    if (error) {
      setMailModifying(false);
      return setErrorMessage('發送失敗，請重新嘗試');
    }
    setChargingData(prev => ({ ...prev, ...data }));
    setErrorMessage('');
    setEmail('');
    setPhone('');
    setMailModifying(false);
    setModifyEmailDialogShow(false);
  };

  const openModifyEmailDialog = () => {
    // 處理 null、undefined 或字串 "null"/"<nil>" 的情況
    const safeEmail =
      chargingData.email &&
      chargingData.email !== 'null' &&
      chargingData.email !== '<nil>' &&
      chargingData.email !== 'undefined'
        ? chargingData.email
        : '';

    const rawPhone = chargingData.userPhone || chargingData.UserPhone;
    const safePhone =
      rawPhone && rawPhone !== 'null' && rawPhone !== '<nil>' && rawPhone !== 'undefined' ? rawPhone : '';

    setEmail(safeEmail);
    setPhone(safePhone);
    setModifyEmailDialogShow(true);
  };

  return {
    chargingData,
    loaded,
    setLoaded,
    email,
    setEmail,
    phone,
    setPhone,
    modifyEmailDialogShow,
    setModifyEmailDialogShow,
    openModifyEmailDialog,
    modifyContact,
    errorMessage,
    setErrorMessage,
    fetchChargingRecord,
    refreshing,
    setRefreshing,
    geolocationFetchFail,
    geolocation,
    mailModifying,
    NavigationMapDialog,
    fetchGeoLocation,
  };
};

const [ChargingRecordsProvider, useChargingRecordsProvider] = constate(UseChargingRecords);

export { ChargingRecordsProvider, useChargingRecordsProvider };
